import type { WordSnapshot } from "../anchor/snapshot";
import type { WordArtifacts } from "../chat/artifacts";
import type { Product, ProductConfig } from "../config";

export interface PairStart {
  pairId: string;
  pollSecret: string;
  /** As the server displays it (e.g. "ABCD-EFGH"). */
  userCode: string;
  /** Epoch seconds. */
  expiresAt: number;
  pollIntervalMs: number;
}

export type PollResult =
  | { status: "pending" }
  | { status: "approved"; token: string; expiresAt: number | null }
  | { status: "consumed" | "expired" | "denied" }
  | { status: "slow_down"; retryAfterMs: number };

export interface SessionInfo {
  email: string;
  product: Product;
}

/** One product's Word add-in endpoints (spec §6). Every request uses `credentials: 'omit'`. */
export interface WordBackend {
  readonly config: ProductConfig;
  startPair(): Promise<PairStart>;
  pollPair(pairId: string, pollSecret: string): Promise<PollResult>;
  getSession(token: string): Promise<SessionInfo>;
  /** Refresh the parent web session; call only on explicit user actions (send, apply, open card). */
  activity(token: string): Promise<void>;
  revoke(token: string): Promise<void>;
}

export class BackendError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly retryAfterMs: number | null = null,
  ) {
    super(message);
    this.name = "BackendError";
  }
}

/** A token (or session) that belongs to the other product, or is not a Word add-in token at all. */
export class WrongProductError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WrongProductError";
  }
}

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

// ---- Word turns (spec §2.1, §3.1, §7.1) ---------------------------------------------------------------------

/** Server-minted snapshot id + presigned S3 POST (`…/word-addin/snapshots/init`). */
export interface SnapshotTicket {
  snapshotId: string;
  url: string;
  fields: Record<string, string>;
  maxBytes: number;
  /** Seconds the presigned POST stays valid. */
  expiresIn: number;
}

export interface UploadReport {
  rawBytes: number;
  gzipBytes: number;
  ms: number;
}

/** What the user did in Word with one card of the previous turn (spec §2.1 step 3). Exactly one id is set. */
export type ApplyOutcome = "applied" | "clean" | "dismissed" | "stale" | "failed";

export interface ApplyReportItem {
  edit_id?: string;
  comment_id?: string;
  insert_id?: string;
  pid?: string;
  outcome: ApplyOutcome;
  /** At most 200 characters. */
  reason?: string;
}

/** The server's caps on `last_apply_report` (`WordTurnRef` in app/handlers/scholar_chat.py). */
export const APPLY_REPORT_MAX_ITEMS = 60;
export const APPLY_REPORT_REASON_MAX = 200;

/** Body of `POST /v1/scholar/chat/jobs` for a Word turn. */
export interface JobCreate {
  message: string;
  word: { snapshot_id: string; last_apply_report?: ApplyReportItem[] };
  thread_id?: string;
}

/** A saved version of the open document (spec §3.3), keyed by (owner, thread, doc_key). */
export interface SavedVersion {
  versionId: string;
  name: string;
  docKey: string;
  threadId: string;
  /** ISO time. */
  createdAt: string;
  docHash: string;
  charCount: number;
  paragraphCount: number;
  /** Word comment ids the pane created while this version was current ("Remove Ella's comments"). */
  ellaCommentIds: string[];
}

export interface SaveVersionRequest {
  snapshotId: string;
  docKey: string;
  threadId: string;
  name: string;
}

export interface JobActivity {
  id: string;
  kind: string;
  outcome: string;
  subject?: string;
  count?: number;
  note?: string;
}

export interface JobEvent {
  type: string;
  message: string;
  stage?: string;
}

export interface JobCitation {
  num: number | null;
  title: string;
  url: string | null;
  court: string | null;
}

/** One poll of a chat job, normalised from the untrusted wire payload. */
export interface JobPoll {
  requestId: string;
  /** Only if the server names it; otherwise a new thread's id is the first job's request id (§7.1). */
  threadId: string | null;
  status: string;
  stage: string;
  terminal: boolean;
  pollAfterMs: number;
  events: JobEvent[];
  activity: JobActivity[];
  thought: string | null;
  draftAnswer: string | null;
  answer: string | null;
  citations: JobCitation[];
  wordArtifacts: WordArtifacts | null;
  errorCode: string | null;
  errorMessage: string | null;
  degradationReasons: string[];
}

export interface WaitOptions {
  onUpdate?: (poll: JobPoll) => void;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  /** Give up after this long (a last poll is made first). */
  maxWaitMs?: number;
}

/** A product's chat-job endpoints for Word turns. Every request uses `credentials: 'omit'`. */
export interface ChatBackend {
  readonly config: ProductConfig;
  initSnapshot(token: string): Promise<SnapshotTicket>;
  /** gzip the snapshot and POST it to the presigned URL (no bearer token goes to S3). */
  uploadSnapshot(ticket: SnapshotTicket, snapshot: WordSnapshot): Promise<UploadReport>;
  createJob(token: string, request: JobCreate): Promise<JobPoll>;
  pollJob(token: string, requestId: string): Promise<JobPoll>;
  /** Poll (honouring `poll_after_ms`) until the job is terminal. */
  waitForJob(token: string, first: JobPoll, options?: WaitOptions): Promise<JobPoll>;
  /** Versions of this document; with no `threadId`, every conversation's (each version names its thread). */
  listVersions(token: string, docKey: string, threadId?: string): Promise<SavedVersion[]>;
  /** Save an uploaded snapshot as a named version of (thread, doc_key). */
  saveVersion(token: string, request: SaveVersionRequest): Promise<SavedVersion>;
  deleteVersion(token: string, versionId: string): Promise<void>;
  /** Remember (`add`) or forget (`remove`) Word comment ids on a version; returns the version as stored. */
  updateEllaComments(token: string, versionId: string, add: readonly string[], remove: readonly string[]): Promise<SavedVersion>;
}

/** The Word token was rejected (401): the pane must reconnect. */
export class AuthLostError extends Error {
  constructor(message = "Your Word session ended. Reconnect to continue.") {
    super(message);
    this.name = "AuthLostError";
  }
}

/** The snapshot could not be compressed or uploaded; `message` is written for the user. */
export class UploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UploadError";
  }
}
